import React from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from "@mui/material";
import moment from "moment";
import produce from "immer";
import startCase from "lodash/startCase";
import { bindActionCreators } from "redux";
import { useDispatch, useSelector } from "react-redux";

import { RootState } from "../../../../store";
import { t, tl } from "../../../../components/translate";
import { resourceCentreActions } from "../../../../actions";
import DefaultServiceSelector from "../../../Calendar/DefaultServiceSelector";
import { ResourceCentre } from "../../../../interfaces/ResourceCentreInterface";
import useCurrentResourceCentre from "../../../../hooks/useCurrentResourceCentre";
import { bsMonths } from "../../../../components/Calendar/functions/calendarData";

interface Props {
  onViewChange: () => void;
}

export enum DEFAULT_CALENDAR_VIEW {
  MONTHLY = "MONTHLY",
  LIST = "LIST"
}

const typesOfCalendar = [
  { value: "ad", label: "Gergorian Calendar(AD)" },
  { value: "bs", label: "Bikram Sambat(BS)" }
];

export const CalendarSettings = ({ onViewChange }: Props): React.ReactNode => {
  const dispatch = useDispatch();
  const resourceCentre = useCurrentResourceCentre();

  // selectors
  const permissionGroup = useSelector(
    (state: RootState) => state.userContext?.userCreds?.userGroups[0]
  );

  // variables
  const disableEditCalendarAndMonth = !["superAdmin"].includes(permissionGroup);
  const months = resourceCentre.localCalendar ? bsMonths(t) : moment.months();

  // helper functions
  const actions = bindActionCreators({ ...resourceCentreActions }, dispatch);
  const updateRc = (rc: ResourceCentre) => {
    dispatch(resourceCentreActions.putResourceCentre(rc));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <FormControl sx={{ flexDirection: "row", gap: 2, alignItems: "center" }}>
          <FormLabel>Default View</FormLabel>
          <RadioGroup
            row
            name="calendar-default-view"
            value={resourceCentre.settings.calendarSettings.defaultView}
            onChange={(_, value) => {
              actions.putResourceCentre(
                produce(resourceCentre, (draft) => {
                  onViewChange();
                  draft.settings.calendarSettings.defaultView = value as DEFAULT_CALENDAR_VIEW;
                })
              );
            }}
          >
            <FormControlLabel
              value={DEFAULT_CALENDAR_VIEW.MONTHLY}
              control={<Radio size="small" />}
              label={startCase(DEFAULT_CALENDAR_VIEW.MONTHLY)}
            />
            <FormControlLabel
              value={DEFAULT_CALENDAR_VIEW.LIST}
              control={<Radio size="small" />}
              label={startCase(DEFAULT_CALENDAR_VIEW.LIST)}
            />
          </RadioGroup>
        </FormControl>
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <TextField
          fullWidth
          disabled={disableEditCalendarAndMonth}
          variant="outlined"
          label={tl("accounts.primaryCalendar")}
          select
          value={resourceCentre.localCalendar ? "bs" : "ad"}
          onChange={({ target }) => {
            updateRc({ ...resourceCentre, localCalendar: target.value === "bs" });
          }}
        >
          {typesOfCalendar.map((calendar) => (
            <MenuItem key={calendar.value} value={calendar.value}>
              {calendar.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          disabled={disableEditCalendarAndMonth}
          variant="outlined"
          label={tl("accounts.startingMonth")}
          select
          value={resourceCentre.fiscalStartMonth || 0}
          onChange={({ target }) => {
            updateRc({ ...resourceCentre, fiscalStartMonth: Number(target.value) });
          }}
        >
          {months.map((calendar, index) => (
            <MenuItem key={calendar} value={index}>
              {calendar}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography
          variant="subtitle1"
          sx={{ pb: 0.5, fontWeight: 600, borderBottom: 1, borderColor: "divider" }}
        >
          Booking Settings
        </Typography>
        <DefaultServiceSelector
          defaultValue={resourceCentre?.settings?.bookingSettings?.defaultService ?? null}
          onServiceSelect={(value) => {
            actions.putResourceCentre(
              produce(resourceCentre, (draft) => {
                draft.settings.bookingSettings.defaultService = value;
              })
            );
          }}
        />
      </Box>
    </Box>
  );
};
